import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { fetchLeads } from "@/lib/api";

const badge = {
  booking: "bg-lime text-black",
  audit: "bg-black text-lime",
  quote: "bg-black text-white",
  contact: "bg-white text-black",
};

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setLeads(await fetchLeads());
    } catch {
      setError("Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section className="px-5 md:px-10 py-16 md:py-24 min-h-[60vh]" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="kicker mb-3">
            <span className="bg-black text-lime px-2 py-1">Admin</span>
          </p>
          <h1 className="font-display text-4xl md:text-6xl uppercase" data-testid="admin-heading">
            Lead trail
          </h1>
        </div>
        <button onClick={load} data-testid="admin-refresh-button" className="btn-brutal-white text-xs">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading && <p className="font-mono text-sm uppercase tracking-widest" data-testid="admin-loading">Loading leads...</p>}
      {error && <p className="font-mono text-sm uppercase tracking-widest text-red-600" data-testid="admin-error">{error}</p>}

      {!loading && !error && leads.length === 0 && (
        <div className="card-brutal p-10 shadow-[6px_6px_0_0_#CCFF00]" data-testid="admin-empty">
          <p className="font-display text-2xl uppercase">No leads yet.</p>
          <p className="font-mono text-xs uppercase tracking-widest mt-2 text-neutral-500">
            Submissions from the booking modal and contact form will march in here.
          </p>
        </div>
      )}

      {!loading && !error && leads.length > 0 && (
        <div className="overflow-x-auto border-2 border-black shadow-[8px_8px_0_0_#0A0A0A]" data-testid="admin-leads-table">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-black text-lime font-mono text-xs uppercase tracking-widest">
                <th className="px-4 py-3 border border-black">Type</th>
                <th className="px-4 py-3 border border-black">Name</th>
                <th className="px-4 py-3 border border-black">Email</th>
                <th className="px-4 py-3 border border-black">Phone</th>
                <th className="px-4 py-3 border border-black">Details</th>
                <th className="px-4 py-3 border border-black">Received</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b-2 border-black hover:bg-lime/40 transition-colors duration-150" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="px-4 py-3 border-r border-black/20">
                    <span className={`font-mono text-[10px] font-bold uppercase tracking-widest border-2 border-black px-2 py-1 ${badge[l.type] || ""}`}>
                      {l.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-r border-black/20 font-bold">{l.name}{l.company ? ` — ${l.company}` : ""}</td>
                  <td className="px-4 py-3 border-r border-black/20 font-mono text-sm">{l.email}</td>
                  <td className="px-4 py-3 border-r border-black/20 font-mono text-sm">{l.phone || "—"}</td>
                  <td className="px-4 py-3 border-r border-black/20 text-sm max-w-xs">
                    {l.service && <span className="block font-mono text-xs uppercase tracking-widest">{l.service}</span>}
                    {l.preferred_date && <span className="block font-mono text-xs">{l.preferred_date} {l.preferred_time}</span>}
                    {l.topic && <span className="block font-mono text-xs uppercase tracking-widest">{l.topic}</span>}
                    {l.website && <span className="block font-mono text-xs">{l.website}</span>}
                    {l.message && <span className="block mt-1">{l.message}</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
